/**
 * Shared date formatting utilities.
 */

/** Relative time format: "Just now", "5m ago", "2h ago", "3d ago" */
export const formatRelativeDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

/** Absolute date in en-US locale: "Mar 25, 2026" */
export const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** Absolute date+time in en-US locale: "Mar 25, 2026, 02:30 PM" */
export const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Korean locale date+time: "3월 25일 오후 02:30" */
export const formatDateKo = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Nullable date with fallback to '-' */
export const formatDateNullable = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

/** Server timestamps are UTC; operators read Korean time. "10월 22일 오후 02:03" */
export const formatKst = (dateStr: string): string =>
  new Date(dateStr).toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Date only, in Korean time: "2026년 3월 20일" */
export const formatKstDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/** Korean relative time: "방금", "3분 전", "2시간 전", "5일 전", "3개월 전". */
export const formatRelativeKo = (dateStr: string, now: Date = new Date()): string => {
  const diffMins = Math.floor((now.getTime() - new Date(dateStr).getTime()) / 60000);
  const diffDays = Math.floor(diffMins / (60 * 24));
  if (diffMins < 1) return '방금';
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffMins < 60 * 24) return `${Math.floor(diffMins / 60)}시간 전`;
  if (diffDays < 60) return `${diffDays}일 전`;
  return `${Math.floor(diffDays / 30)}개월 전`;
};
